import React, { useState } from 'react';
import api from '../../services/api';
import { MetricForm } from './MetricPicker';
import { metricHeading, errorText } from './format';
import { pillarColour } from './palette';
import { toDraft, draftPayload as payload } from './weighting';

const BASE = '/api/quant-screener/weighting-sets';

const emptyDraft = () => ({ name: '', description: '', score_method: 'percentile', pillars: [] });

function download(blob, fallback, headers) {
  const m = /filename="([^"]+)"/.exec(headers?.['content-disposition'] || '');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = m ? m[1] : fallback; a.click();
  URL.revokeObjectURL(url);
}

// Weighting sets: pick the set that scores the screen, edit a draft, save it as a new set or a
// new version, make it the Drummond Standard, upload a filled template, download template / set.
export default function ScoreBuilder({ catalogue, universe, sets, reloadSets, scoreMode, setScoreMode, draft, setDraft, onApplyDraft }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null);
  const [upload, setUpload] = useState({ file: null, name: '' });

  const run = async (fn, okMsg) => {
    setBusy(true); setErr(null); setMsg(null);
    try { const out = await fn(); if (okMsg) setMsg(okMsg); return out; } catch (e) { setErr(errorText(e)); return null; } finally { setBusy(false); }
  };

  const startEdit = (ws) => { setDraft(toDraft(ws)); setHistory(null); };
  const startNew = () => { setDraft(emptyDraft()); setHistory(null); };

  const setP = (i, patch) => setDraft((d) => ({ ...d, pillars: d.pillars.map((p, k) => (k === i ? { ...p, ...patch } : p)) }));
  const addPillar = () => setDraft((d) => ({ ...d, pillars: [...d.pillars, { name: `Pillar ${d.pillars.length + 1}`, weight: 1, metrics: [] }] }));
  const removePillar = (i) => setDraft((d) => ({ ...d, pillars: d.pillars.filter((_, k) => k !== i) }));
  const addMetric = (i, m) => setP(i, { metrics: [...draft.pillars[i].metrics, { ...m, weight: 1 }] });
  const setMetric = (i, j, patch) => setP(i, { metrics: draft.pillars[i].metrics.map((m, k) => (k === j ? { ...m, ...patch } : m)) });
  const removeMetric = (i, j) => setP(i, { metrics: draft.pillars[i].metrics.filter((_, k) => k !== j) });

  const saveNew = () => run(async () => {
    const r = await api.post(BASE, { ...payload(draft), set_as_standard: false });
    await reloadSets(); setDraft(toDraft(r.data)); setScoreMode(String(r.data.id));
  }, 'Saved as a new weighting set.');
  const saveVersion = () => run(async () => {
    const r = await api.put(`${BASE}/${draft.id}`, { ...payload(draft), set_as_standard: false });
    await reloadSets(); setDraft(toDraft(r.data)); setScoreMode(String(r.data.id));
  }, 'Saved as a new version (the previous version is kept in history).');
  const makeStandard = (id) => run(async () => { await api.post(`${BASE}/${id}/standard`); await reloadSets(); }, 'Drummond Standard updated.');
  const remove = (id) => run(async () => {
    await api.delete(`${BASE}/${id}`); await reloadSets();
    if (scoreMode === String(id)) setScoreMode('standard');
    if (draft?.id === id) setDraft(null);
  }, 'Weighting set removed.');
  const loadHistory = (id) => run(async () => { const r = await api.get(`${BASE}/${id}/history`); setHistory(r.data); });
  const downloadTemplate = () => run(async () => {
    const r = await api.get(`${BASE}/template.xlsx`, { responseType: 'blob' });
    download(r.data, 'quant-screener-weighting-template.xlsx', r.headers);
  });
  const exportSet = (id, name) => run(async () => {
    const r = await api.get(`${BASE}/${id}/export.xlsx`, { responseType: 'blob' });
    download(r.data, `${name}.xlsx`, r.headers);
  });
  const doUpload = () => run(async () => {
    const fd = new FormData();
    fd.append('file', upload.file); fd.append('name', upload.name || upload.file.name.replace(/\.(xlsx|csv)$/i, ''));
    const r = await api.post(`${BASE}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    const d = toDraft({ ...r.data, id: undefined });
    setDraft(d); setUpload({ file: null, name: '' });
    return r.data;
  }, 'Template parsed. Review the draft, then save it.');

  const standard = sets.find((s) => s.is_standard);
  const pillarTotal = draft ? draft.pillars.reduce((a, p) => a + (Number(p.weight) || 0), 0) : 0;

  return (
    <div className="qs-score">
      <div className="qs-score-list">
        <div className="qs-filter-title">Score the screen with</div>
        <label className="qs-radio"><input type="radio" name="qs-score" checked={scoreMode === 'none'} onChange={() => setScoreMode('none')} /> No score</label>
        {sets.map((s) => (
          <div key={s.id} className={`qs-set${s.is_standard ? ' qs-set-std' : ''}`}>
            <label className="qs-radio">
              <input type="radio" name="qs-score" checked={scoreMode === String(s.id) || (scoreMode === 'standard' && s.is_standard)} onChange={() => setScoreMode(s.is_standard ? 'standard' : String(s.id))} />
              {s.is_standard && <span className="qs-star" title="Drummond Standard">★</span>} {s.name}
            </label>
            <div className="qs-set-meta">
              {s.pillars.length} pillars · {s.pillars.reduce((a, p) => a + p.metrics.length, 0)} metrics · {s.score_method}
              {s.created_by_email ? ` · ${s.created_by_email}` : ''}
            </div>
            <div className="qs-set-actions">
              <button type="button" className="qs-link" onClick={() => startEdit(s)}>edit</button>
              {!s.is_standard && <button type="button" className="qs-link" onClick={() => makeStandard(s.id)} disabled={busy}>set as standard</button>}
              <button type="button" className="qs-link" onClick={() => exportSet(s.id, s.name)} disabled={busy}>xlsx</button>
              <button type="button" className="qs-link" onClick={() => loadHistory(s.id)} disabled={busy}>history</button>
              {!s.is_standard && <button type="button" className="qs-link qs-danger" onClick={() => remove(s.id)} disabled={busy}>delete</button>}
            </div>
          </div>
        ))}
        {draft && (
          <label className="qs-radio qs-draft-radio">
            <input type="radio" name="qs-score" checked={scoreMode === 'draft'} onChange={() => setScoreMode('draft')} /> Draft (unsaved){draft.name ? `: ${draft.name}` : ''}
          </label>
        )}
        <div className="qs-set-tools">
          <button type="button" className="qs-btn" onClick={startNew}>New set</button>
          <button type="button" className="qs-btn qs-btn-ghost" onClick={downloadTemplate} disabled={busy}>Download template</button>
        </div>
        <div className="qs-upload">
          <div className="qs-filter-title">Upload a filled template</div>
          <input type="file" accept=".xlsx,.csv" onChange={(e) => setUpload((u) => ({ ...u, file: e.target.files?.[0] || null }))} />
          <input type="text" placeholder="name for the set" value={upload.name} onChange={(e) => setUpload((u) => ({ ...u, name: e.target.value }))} />
          <button type="button" className="qs-btn" onClick={doUpload} disabled={!upload.file || busy}>Parse</button>
          <div className="qs-muted">Parsing validates and shows a draft; nothing is saved until you save it.</div>
        </div>
        {history && (
          <div className="qs-history">
            <div className="qs-filter-title">History <button type="button" className="qs-link" onClick={() => setHistory(null)}>close</button></div>
            {history.map((h) => (
              <div key={h.id} className="qs-history-row">
                <span>#{h.id} {h.name}</span>
                <span className="qs-muted">{h.created_at ? h.created_at.slice(0, 16).replace('T', ' ') : ''} {h.created_by_email || ''} {h.is_active ? '(current)' : ''}</span>
                {!h.is_active && <button type="button" className="qs-link" onClick={() => startEdit(h)}>open as draft</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="qs-score-editor">
        {msg && <div className="qs-ok">{msg}</div>}
        {err && <div className="qs-alert">{err}</div>}
        {!draft && (
          <div className="qs-muted qs-editor-empty">
            Pick a set on the left and choose <b>edit</b>, or start a new one. The Drummond Standard is
            {standard ? ` "${standard.name}"` : ' not set'}; every edit is saved as a new version, nothing is overwritten.
          </div>
        )}
        {draft && (
          <>
            <div className="qs-editor-head">
              <input id="qs-ws-name" type="text" placeholder="Weighting set name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              <input id="qs-ws-desc" type="text" placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              <select value={draft.score_method} onChange={(e) => setDraft({ ...draft, score_method: e.target.value })} title="How a metric becomes a 0-100 score">
                {(catalogue?.score_methods || ['percentile']).map((m) => <option key={m} value={m}>{m === 'band' ? 'band (Ben, 1-5 on ratio to cohort mean)' : m}</option>)}
              </select>
            </div>
            {draft.pillars.map((p, i) => {
              const mTotal = p.metrics.reduce((a, m) => a + (Number(m.weight) || 0), 0);
              return (
                <div key={i} className="qs-pillar" style={{ borderLeftColor: pillarColour(i) }}>
                  <div className="qs-pillar-head">
                    <input type="text" value={p.name} onChange={(e) => setP(i, { name: e.target.value })} className="qs-pillar-name" />
                    <label className="qs-weight">weight <input type="number" min={0} step={0.05} value={p.weight} onChange={(e) => setP(i, { weight: e.target.value })} />
                      <span className="qs-muted">{pillarTotal > 0 ? `${((Number(p.weight) || 0) / pillarTotal * 100).toFixed(0)}%` : ''}</span></label>
                    <button type="button" className="qs-link qs-danger" onClick={() => removePillar(i)}>remove pillar</button>
                  </div>
                  <table className="qs-pillar-metrics">
                    <tbody>
                      {p.metrics.map((m, j) => (
                        <tr key={j}>
                          <td className="qs-l">{metricHeading(m, catalogue)}</td>
                          <td className="qs-l"><input type="text" placeholder="label (optional)" value={m.label || ''} onChange={(e) => setMetric(i, j, { label: e.target.value })} /></td>
                          <td className="qs-num"><input type="number" min={0} step={0.05} value={m.weight} onChange={(e) => setMetric(i, j, { weight: e.target.value })} style={{ width: 70 }} />
                            <span className="qs-muted"> {mTotal > 0 ? `${((Number(m.weight) || 0) / mTotal * 100).toFixed(0)}%` : ''}</span></td>
                          <td><button type="button" className="qs-link qs-danger" onClick={() => removeMetric(i, j)}>×</button></td>
                        </tr>
                      ))}
                      {p.metrics.length === 0 && <tr><td colSpan={4} className="qs-muted">No metrics in this pillar yet.</td></tr>}
                    </tbody>
                  </table>
                  <MetricForm catalogue={catalogue} asAtDates={universe?.as_at_dates} onAdd={(m) => addMetric(i, m)} buttonLabel="Add metric" />
                </div>
              );
            })}
            <div className="qs-editor-actions">
              <button type="button" className="qs-btn qs-btn-ghost" onClick={addPillar}>Add pillar</button>
              <button type="button" className="qs-btn" onClick={() => { setScoreMode('draft'); onApplyDraft(); }} disabled={busy}>Apply draft to screen</button>
              <button type="button" className="qs-btn" onClick={saveNew} disabled={busy || !draft.name}>Save as new set</button>
              {draft.id && <button type="button" className="qs-btn" onClick={saveVersion} disabled={busy || !draft.name}>Save as new version</button>}
              <button type="button" className="qs-link" onClick={() => setDraft(null)}>discard draft</button>
            </div>
            <div className="qs-muted">Weights are normalised proportionally within each pillar and across pillars. A fund missing a metric is scored on the rest and flagged with the count used.</div>
          </>
        )}
      </div>
    </div>
  );
}
